/**
 * When the IAP assertion needs renewing - which is not when the session ends.
 *
 * The session route returns `expiresAt`, and the contract says exactly what it
 * is: "Expiration of the signature-verified IAP assertion". That is the header
 * IAP mints for each request, not the login. It is short - minutes - and IAP
 * issues a fresh one on the next request without the user doing anything.
 *
 * This file read it as the end of the session. So the product announced "your
 * session ends in 9 minutes" over and over to someone who was never going to be
 * logged out, could never see the warning more than ten minutes ahead because
 * that was the whole window, and watched a countdown for an event that does not
 * happen. Then a later change acted on it and sent a fully signed-in operator
 * to the sign-in screen.
 *
 * What is true: the only proof a session has ended is a request that was
 * refused. Everything here now serves one purpose - renewing the assertion
 * quietly before it lapses, so no request is ever refused for a stale one.
 */

/** Renew this long before the assertion lapses, so no request races it. */
export const RENEW_BEFORE_MS = 60_000;

/**
 * Never renew more often than this.
 *
 * A backend that keeps reporting an assertion as already lapsed - a clock skew
 * between here and there is enough - would otherwise schedule the next renewal
 * at zero milliseconds, and the app would sit in a tight loop re-reading the
 * session for as long as the tab was open. The floor turns that failure into a
 * slow poll instead of a flood.
 */
export const MIN_RENEW_MS = 15_000;

/**
 * How often returning to the tab is allowed to re-check the session.
 *
 * Focus fires on every alt-tab, and each check is a request. This is the
 * shortest gap worth spending one on.
 */
export const FOCUS_CHECK_MS = 30_000;

export interface Expiry {
  /** The assertion is close enough to lapsing to renew it now. */
  renewNow: boolean;
  /** The assertion has lapsed. The next request renews it; nobody is signed out. */
  lapsed: boolean;
  /** Milliseconds until it should be renewed, floored at zero. */
  renewInMs: number;
  /** The assertion's end, for scheduling. */
  at: Date | null;
}

export function readExpiry(expiresAt: string | null | undefined, now = Date.now()): Expiry {
  const none: Expiry = { renewNow: false, lapsed: false, renewInMs: 0, at: null };
  if (!expiresAt) return none;
  const at = new Date(expiresAt);
  const time = at.getTime();
  if (Number.isNaN(time)) return none;

  const remaining = time - now;
  return {
    renewNow: remaining <= RENEW_BEFORE_MS,
    lapsed: remaining <= 0,
    renewInMs: Math.max(MIN_RENEW_MS, remaining - RENEW_BEFORE_MS),
    at,
  };
}
